import Chip from '@mui/material/Chip';
import { Fragment } from 'react';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import type { Contentful } from '@/contentful/contentful-exports';
import useGlobalStyles from '@/styles/global.styles';

import BulletedList from './BulletList';
import useStyles from './Cards.styles';
import IconListItems, { ListType } from './IconListItems';

export interface BulletListContentProps extends Contentful {
  fields: {
    bulletedListTitle?: string;
    listItems?: string[];
  };
}

type Card = {
  title: string;
  subheading: string;
  featuredListTitle: string;
  featuredList: {
    fields: {
      additionalItems?: string[] | undefined;
      includedItems?: string[] | undefined;
      excludedItems?: string[] | undefined;
    };
  };
  bulletedList: string[];
  bulletedListTitle: string;
  secondBulletedList?: string[];
  secondBulletedListTitle?: string;
  bulletList?: BulletListContentProps[];
  primaryCta?: {
    fields: {
      text: string;
      url: string;
    };
  };
  secondaryCta?: {
    fields: {
      text: string;
      url: string;
    };
  };
  chip?: string;
};

export interface CardFields {
  fields: Card;
}

const Cards = ({
  cards,
  isOnDark,
  selectedTab,
}: {
  cards: CardFields[];
  isOnDark: boolean;
  selectedTab?: number;
}) => {
  const cardNumber = cards.length;
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const { classes, cx } = useStyles({ isOnDark, cardNumber });
  const cardArray = selectedTab
    ? [cards[selectedTab], cards[0], ...cards.slice(2)]
    : cards;
  return (
    <div
      className={cx(classes.wrapper, {
        // biome-ignore lint/suspicious/noDoubleEquals: Automated migration to Biome
        [classes.wrapper2up]: cards.length == 2,
        // biome-ignore lint/suspicious/noDoubleEquals: Automated migration to Biome
        [classes.wrapper3up]: cards.length == 3,
      })}
    >
      {cardArray.map(
        (
          {
            fields: {
              title,
              subheading,
              featuredList,
              bulletedList,
              secondBulletedList,
              chip,
              primaryCta,
              secondaryCta,
              featuredListTitle,
              bulletedListTitle,
              secondBulletedListTitle,
              bulletList,
            },
          },
          index
        ) => {
          const cardClass = `card${index + 1}` as 'card1' | 'card2' | 'card3';
          const primaryCTAFields = primaryCta?.fields;
          const secondaryCTAFields = secondaryCta?.fields;
          const featureListFields = featuredList?.fields;
          const { additionalItems, includedItems, excludedItems } =
            featureListFields;
          const cardNumber = `card-${index + 1}`;
          return (
            <Fragment key={cardNumber}>
              <div
                className={cx(
                  classes[cardClass],
                  classes.item1,
                  classes.box,
                  classes.chipContainer
                )}
                data-testid={cardNumber}
              >
                {chip && <Chip label={chip} className={classes.chip} />}
              </div>
              {/* Title and subhead*/}
              <div
                className={cx(classes[cardClass], classes.box, classes.item2)}
              >
                <h3 className={cx(classes.title, globalClasses.h3)}>{title}</h3>
                <p className={cx(classes.subhead, globalClasses.bodyLg)}>
                  {subheading}
                </p>
              </div>

              {/* Featured list  */}
              <div
                className={cx(classes[cardClass], classes.box, classes.item3)}
              >
                <h4 className={cx(classes.listTitle, globalClasses.body)}>
                  {featuredListTitle}
                </h4>
                <ul
                  className={cx(
                    classes.featuredList,
                    classes.list,
                    globalClasses.bodySm
                  )}
                >
                  {includedItems && (
                    <IconListItems
                      items={includedItems}
                      isOnDark={isOnDark}
                      type={ListType.Included}
                    />
                  )}
                  {additionalItems && (
                    <IconListItems
                      items={additionalItems}
                      isOnDark={isOnDark}
                      type={ListType.Additional}
                    />
                  )}
                  {excludedItems && (
                    <IconListItems
                      items={excludedItems}
                      isOnDark={isOnDark}
                      type={ListType.Excluded}
                    />
                  )}
                </ul>
              </div>

              {/* Bulleted list  */}
              <div
                className={cx(classes[cardClass], classes.box, classes.item4)}
              >
                {!bulletList || bulletList.length === 0 ? (
                  <Fragment>
                    <BulletedList
                      classes={classes}
                      bulletedListTitle={bulletedListTitle}
                      bulletedList={bulletedList}
                    />
                    {secondBulletedList && (
                      <div className={classes.secondListContainer}>
                        <BulletedList
                          classes={classes}
                          bulletedListTitle={secondBulletedListTitle}
                          bulletedList={secondBulletedList}
                        />
                      </div>
                    )}
                  </Fragment>
                ) : (
                  <Fragment>
                    <BulletedList
                      classes={classes}
                      bulletedListTitle={
                        bulletList[0]?.fields?.bulletedListTitle
                      }
                      bulletedList={bulletList[0]?.fields?.listItems}
                    />
                    {bulletList.slice(1).map((item, index) => (
                      <div
                        key={`bulletList-${
                          // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                          index
                        }`}
                        className={classes.secondListContainer}
                      >
                        <BulletedList
                          classes={classes}
                          bulletedListTitle={item?.fields?.bulletedListTitle}
                          bulletedList={item?.fields?.listItems}
                        />
                      </div>
                    ))}
                  </Fragment>
                )}
              </div>

              {/* Buttons */}

              <div
                className={cx(classes[cardClass], classes.box, classes.item5)}
              >
                {((primaryCta && primaryCTAFields) ||
                  (secondaryCta && secondaryCTAFields)) && (
                  <div className={classes.buttonContainer}>
                    <div className={classes.buttonInnerContainer}>
                      {primaryCta && primaryCTAFields && (
                        <Button
                          variant={ButtonVariant.SECONDARY}
                          label={primaryCTAFields?.text}
                          href={primaryCTAFields?.url}
                          isOnDark={isOnDark}
                          size={ButtonSize.SM}
                        />
                      )}
                      {secondaryCta && secondaryCTAFields && (
                        <Button
                          variant={ButtonVariant.TERTIARY}
                          label={secondaryCTAFields.text}
                          href={secondaryCTAFields.url}
                          isOnDark={isOnDark}
                          size={ButtonSize.SM}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Fragment>
          );
        }
      )}
    </div>
  );
};

export default Cards;
